/**
 * BlindModeToggle — Sprint 2 / TASK 4-2
 *
 * Pill-style toggle that sits in the WorkspaceShell UtilityCluster.
 * When ON, all `JobCard` and `JobDetailDrawer` instances replace company names
 * with stable anonymous labels ("Company A", "Company B", …).
 *
 * Reads/writes `user_profiles.blind_mode` via `useBlindMode`.
 */

import { EyeOff } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useBlindMode } from '../../dashboard/hooks/useBlindMode';

const BlindModeToggle: React.FC = () => {
  const { t } = useTranslation();
  const { blindMode, loading, toggle } = useBlindMode();

  const isOn = blindMode ?? false;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={loading}
      title={
        isOn
          ? t('shell.blindMode.disable', 'Disable blind mode')
          : t('shell.blindMode.enable', 'Enable blind mode')
      }
      aria-label={
        isOn
          ? t('shell.blindMode.disable', 'Disable blind mode')
          : t('shell.blindMode.enable', 'Enable blind mode')
      }
      aria-pressed={isOn}
      className={[
        'flex h-7 min-h-[44px] items-center gap-1 px-1.5 md:px-2 rounded-md text-xs font-medium transition-colors touch-manipulation',
        'disabled:opacity-40 disabled:cursor-wait',
        isOn
          ? 'bg-[var(--brand-purple,#6C47FF)] text-white hover:bg-[var(--brand-purple-hover,#5535E0)]'
          : 'border-0 bg-transparent text-slate-600 hover:opacity-80 dark:text-slate-300 md:border md:border-slate-100 md:bg-slate-50 md:hover:bg-slate-100 md:dark:border-slate-700 md:dark:bg-slate-800/60',
      ].join(' ')}
    >
      <EyeOff className="w-3.5 h-3.5 shrink-0" aria-hidden />
      <span className="hidden sm:inline whitespace-nowrap">
        {t('shell.blindMode.label', 'Blind')}
      </span>
    </button>
  );
};

export default BlindModeToggle;
